import React, { useState } from 'react';
import { AlertTriangle, Download, FileText, ImageOff, RotateCw, User, Users } from 'lucide-react';
import { cn, formatFileSize, getAgentTextColorClass } from '../../utils';
import type { Attachment, Message } from '../../types';
import AgentAvatar from '../Common/AgentAvatar';
import MarkdownRenderer from './MarkdownRenderer';
import MessageMeta from './MessageMeta';

interface GroupSender {
  id: string;
  name: string;
  avatar?: string;
  type: 'agent' | 'user';
}

interface GroupMessageBubbleProps {
  message: Message;
  sender: GroupSender | null;
  isGrouped?: boolean;
  className?: string;
  timeReference: number;
  onRetry?: (messageId: string) => void;
  onDismiss?: (messageId: string) => void;
}

function renderContentWithMentions(content: string) {
  return content.split(/(@[^\s]+)/g).map((part, index) =>
    part.startsWith('@') ? (
      <span key={`${part}-${index}`} className="font-medium text-blue-300">
        {part}
      </span>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    ),
  );
}

export function GroupMessageBubble({
  message,
  sender,
  isGrouped = false,
  className,
  timeReference,
  onRetry,
  onDismiss,
}: GroupMessageBubbleProps) {
  const isAgent = sender?.type === 'agent';
  const isStreaming = message.status === 'streaming';
  const streamingState = typeof message.metadata?.streamingState === 'string' ? message.metadata.streamingState : 'streaming';
  const streamingLabel = streamingState === 'starting'
    ? null
    : streamingState === 'recovering'
      ? 'Recovering stream...'
      : null;
  const senderName = sender?.name || 'Unknown';

  return (
    <div className={cn('flex gap-3', className)}>
      <div className={cn('shrink-0', isGrouped ? 'w-8' : '')}>
        {!isGrouped ? (
          isAgent ? (
            <AgentAvatar agent={{ name: senderName, avatar: sender?.avatar }} size="sm" />
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-[10px] font-semibold text-white ring-1 ring-white/10">
              {senderName.charAt(0).toUpperCase() || '?'}
            </div>
          )
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        {!isGrouped ? (
          <div className="mb-1 flex items-center gap-2">
            {isAgent ? <Users className="h-3.5 w-3.5 text-muted-foreground" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
            <span className={cn('text-xs font-medium', isAgent ? getAgentTextColorClass(sender?.id || senderName) : 'text-blue-300')}>
              {senderName}
            </span>
          </div>
        ) : null}

        <div className="flex flex-col items-start">
          <div
            className={cn(
              'w-fit max-w-[min(100%,44rem)] min-w-[80px] rounded-[1.4rem] rounded-bl-lg border px-4 py-3 shadow-sm lg:max-w-[60%]',
              message.isError
                ? 'border-amber-500/25 bg-amber-500/[0.08]'
                : !isAgent && message.status === 'failed'
                  ? 'border-red-500/40 bg-red-500/10'
                  : isAgent
                    ? 'border-white/10 bg-white/[0.04]'
                    : 'border-border bg-card',
              isGrouped && 'mt-1',
            )}
          >
            {message.attachments?.filter((attachment) => attachment.contentType?.startsWith('image/')).length ? (
              <div className="mb-2 space-y-2">
                {message.attachments
                  ?.filter((attachment) => attachment.contentType?.startsWith('image/'))
                  .map((attachment) => <GroupImageAttachment key={attachment.id} attachment={attachment} />)}
              </div>
            ) : null}

            <div className={cn('flex items-start gap-2 text-sm leading-relaxed', message.isError ? 'text-amber-100' : 'text-foreground')}>
              {message.isError ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" /> : null}
              <div className="min-w-0 flex-1 whitespace-pre-wrap break-words">
                {isAgent ? <MarkdownRenderer content={message.content} /> : renderContentWithMentions(message.content)}
              </div>
              {message.isError ? <RotateCw className="mt-1 h-3.5 w-3.5 shrink-0 text-amber-400/80" /> : null}
            </div>

            {isStreaming ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                {streamingLabel ? <span>{streamingLabel}</span> : null}
                <span className="flex items-center gap-1">
                  <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: '0ms' }} />
                  <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: '150ms' }} />
                  <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-blue-400" style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            ) : null}

            {message.attachments?.filter((attachment) => !attachment.contentType?.startsWith('image/')).length ? (
              <div className="mt-2 space-y-2">
                {message.attachments
                  ?.filter((attachment) => !attachment.contentType?.startsWith('image/'))
                  .map((attachment) => <GroupFileAttachment key={attachment.id} attachment={attachment} />)}
              </div>
            ) : null}
          </div>

          <MessageMeta
            message={message}
            isUser={!isAgent}
            timeReference={timeReference}
            align="left"
            onRetry={onRetry}
            onDismiss={onDismiss}
          />
        </div>
      </div>
    </div>
  );
}

function GroupImageAttachment({ attachment }: { attachment: Attachment }) {
  const [hasError, setHasError] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  if (hasError) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-background/70 p-3">
        <ImageOff className="h-5 w-5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Image unavailable</span>
      </div>
    );
  }

  return (
    <div className="relative max-w-sm overflow-hidden rounded-xl border border-border bg-card/70">
      <img src={attachment.url} alt={attachment.filename} className="max-h-64 w-full cursor-pointer rounded-xl object-cover" onClick={() => setIsExpanded((open) => !open)} onError={() => setHasError(true)} loading="lazy" />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
        <div className="flex items-center justify-between">
          <p className="mr-2 flex-1 truncate text-xs text-white/90">{attachment.filename}</p>
          <a href={attachment.url} download={attachment.filename} className="rounded-md p-1 text-white transition-colors duration-150 hover:bg-white/10 hover:text-white/80" onClick={(event) => event.stopPropagation()} aria-label={`Download ${attachment.filename}`}>
            <Download className="h-3 w-3" />
          </a>
        </div>
      </div>

      {isExpanded ? (
        <div className="fixed inset-0 z-50 flex cursor-pointer items-center justify-center bg-black/80" onClick={() => setIsExpanded(false)}>
          <img src={attachment.url} alt={attachment.filename} className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      ) : null}
    </div>
  );
}

function GroupFileAttachment({ attachment }: { attachment: Attachment }) {
  return (
    <a href={attachment.url} download={attachment.filename} className="flex items-center gap-3 rounded-xl border border-border bg-card/70 p-3 no-underline transition-colors duration-150 hover:bg-background">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-background text-foreground">
        <FileText className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{attachment.filename}</p>
        <p className="text-xs text-muted-foreground">{formatFileSize(attachment.size)}</p>
      </div>
      <Download className="h-4 w-4 text-muted-foreground" />
    </a>
  );
}

const MemoizedGroupMessageBubble = React.memo(GroupMessageBubble, (prevProps, nextProps) => (
  prevProps.message === nextProps.message
  && prevProps.sender === nextProps.sender
  && prevProps.isGrouped === nextProps.isGrouped
  && prevProps.className === nextProps.className
  && prevProps.timeReference === nextProps.timeReference
  && prevProps.onRetry === nextProps.onRetry
  && prevProps.onDismiss === nextProps.onDismiss
));

export default MemoizedGroupMessageBubble;
